import { Box, Stack, type StackProps, Tooltip } from '@mui/material'
import { FC } from 'react'
import { getActivityTypeDisplayName } from './scrubberUtils'

const dividerWidth = 6
const dividerMargin = 2
const totalDividerSpace = dividerWidth + 2 * dividerMargin

type ScrubberSegmentProps = StackProps & {
  index?: number
  activityType: string
  durationSeconds?: number
  relativeStartTimeSeconds?: number
  // a fraction between 0 and 1
  percentageOfTotalTime?: number
  // a fraction between 0 and 1
  viewedPercent?: number
  isPlayedSegment?: boolean
  isPrerollSegment?: boolean
  isDragging?: boolean
  justSeeked?: boolean
  showDivider?: boolean
  isPrimarilyDeadSpace: boolean
}
export const ScrubberSegment: FC<ScrubberSegmentProps> = ({
  activityType,
  index = 0,
  durationSeconds,
  relativeStartTimeSeconds,
  percentageOfTotalTime,
  viewedPercent = 0,
  isPlayedSegment = false,
  isPrerollSegment = false,
  isDragging = false,
  justSeeked = false,
  showDivider = false,
  isPrimarilyDeadSpace,
  sx,
  ...stackProps
}) => {
  return (
    <Tooltip
      title={getActivityTypeDisplayName(activityType)}
      placement="top-start"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [-8, -8]
              }
            }
          ]
        },
        tooltip: {
          sx: {
            color: 'var(--text-inverted)',
            backgroundColor: 'var(--neutral-800-t80)'
          }
        }
      }}
    >
      <Stack
        data-testid={`scrubber-segment-${index}`}
        data-test-duration-seconds={durationSeconds}
        data-test-relative-start-seconds={relativeStartTimeSeconds}
        data-test-is-primarily-dead-space={isPrimarilyDeadSpace}
        direction={'row'}
        sx={{
          alignItems: 'center',
          ...(percentageOfTotalTime ? { width: `${100 * percentageOfTotalTime}%` } : {}),
          ...sx
        }}
        {...stackProps}
      >
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'var(--shape-action-transparent)',
            borderRadius: 'var(--border-radius-l)',
            position: 'relative',
            overflow: 'hidden',
            height: 'var(--size-3xs)',
            ...(isPrimarilyDeadSpace && {
              backgroundColor: 'var(--color-neutral-200)',
              border: '0.0625rem solid var(--shape-action)'
            })
          }}
        >
          {/* Viewed portion of segment */}
          {!isPrimarilyDeadSpace && (
            <Box
              sx={{
                width: showDivider ? `calc(${100 * viewedPercent}% + ${totalDividerSpace * viewedPercent}px)` : `${100 * viewedPercent}%`,
                height: '100%',
                backgroundColor: isPlayedSegment || isPrerollSegment ? 'var(--bg-surface-warning)' : 'var(--shape-action)',
                borderRadius: 'var(--border-radius-l) 0 0 var(--border-radius-l)',
                transition: isDragging || justSeeked ? 'none' : 'width 120ms linear'
              }}
            />
          )}
        </Box>
        {showDivider && (
          <Box
            sx={{
              width: `${dividerWidth}px`,
              height: `${dividerWidth}px`,
              backgroundColor: isPlayedSegment ? 'var(--shape-action)' : 'var(--yellow-500)',
              borderRadius: '50%',
              margin: `0 ${dividerMargin}px`, // 0.125rem spacing on left and right
              flexShrink: 0, // Prevent the divider from shrinking
              cursor: 'pointer' // Show it's interactive
            }}
          />
        )}
      </Stack>
    </Tooltip>
  )
}
